import React, {createContext, useCallback, useContext, useMemo, useRef} from 'react';
import {normalizeWikidataEntityDomEl} from './entityHoverSuppression';

/**
 * Shared refs for mention hover coordination. Intentionally NOT React state — preview
 * documents can contain thousands of entity marks and any context state here would
 * re-render every MentionHoverCard on each mousedown.
 */
const MentionHoverRefsContext = createContext(null);

const QID_RE = /^Q\d+$/i;

export function MentionHoverDismissProvider({children, activePopover = null}) {
    const refs = useRef({
        activePopover: null,
        transientSuppress: false,
        /** Linked mention pressed — bridges mousedown→React state gap without re-renders. */
        pressedMention: null,
    });
    refs.current.activePopover = activePopover;
    if (!activePopover) {
        refs.current.pressedMention = null;
    }

    const stableRefs = useMemo(() => refs.current, []);

    const onMouseDownCapture = useCallback((event) => {
        const linkedMention = event.target instanceof Element
            ? event.target.closest('.wikidata-entity[data-wikidata-id]')
            : null;
        if (linkedMention) {
            const anchorEl = normalizeWikidataEntityDomEl(linkedMention);
            stableRefs.pressedMention = {
                type: 'wikidata',
                anchorEl,
                uid: anchorEl?.getAttribute('data-wikidata-uid')
                    || linkedMention.getAttribute('data-wikidata-uid')
                    || null,
            };
        } else {
            stableRefs.pressedMention = null;
        }
        if (event.target instanceof Element && event.target.closest('[data-mention-hover]')) {
            stableRefs.transientSuppress = true;
        }
    }, [stableRefs]);

    const onMouseOut = useCallback((event) => {
        const mention = event.target instanceof Element
            ? event.target.closest('[data-mention-hover]')
            : null;
        if (!mention) return;
        const next = event.relatedTarget;
        if (!(next instanceof Element) || !mention.contains(next)) {
            stableRefs.transientSuppress = false;
        }
    }, [stableRefs]);

    return (
        <MentionHoverRefsContext.Provider value={stableRefs}>
            <div onMouseDownCapture={onMouseDownCapture} onMouseOut={onMouseOut}>
                {children}
            </div>
        </MentionHoverRefsContext.Provider>
    );
}

export function useMentionHoverRefs() {
    return useContext(MentionHoverRefsContext);
}

/** @deprecated Prefer useMentionHoverRefs().transientSuppress — kept for tests. */
export function useMentionHoverSuppressed() {
    const refs = useMentionHoverRefs();
    return Boolean(refs?.transientSuppress);
}

/** @deprecated Prefer useMentionHoverRefs().activePopover — kept for compatibility. */
export function useActiveEntityPopoverRef() {
    const refs = useMentionHoverRefs();
    return refs ? {current: refs.activePopover} : null;
}
